import styled from '@emotion/styled'
import {COLORS} from '../constants'

/**
 * The clickable investor-count control in the card meta row. Styled to sit inline with the
 * neighbouring meta items but reads as interactive (hover background), and greys out when the
 * offer has no investors.
 */
export const CountButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: -2px -4px;
  padding: 2px 4px;
  border: none;
  border-radius: 6px;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;

  &:hover {
    background-color: ${COLORS.mutedBg};
    color: ${COLORS.foreground};
  }

  &:disabled {
    cursor: default;
    color: inherit;
  }

  &:disabled:hover {
    background: none;
  }
`

/** Popover body wrapper; caps width to the card / viewport and clips the rounded corners. */
export const PopoverBody = styled.div`
  width: min(92vw, 640px);
  color: ${COLORS.foreground};
`

/** Sticky header of the popover: owner + count on the left, AUM on the right. */
export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 16px;
  border-bottom: 1px solid ${COLORS.border};
  background-color: ${COLORS.mutedBg};
`

/** Small uppercase caption used for the "Investors" and "AUM" labels. */
export const HeaderCaption = styled.div`
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${COLORS.mutedText};
`

/** Owner name + total-count line under the "Investors" caption. */
export const HeaderTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.foreground};
`

/** Right-aligned AUM figure in the header. */
export const HeaderAum = styled.div`
  font-size: 14px;
  font-weight: 600;
  text-align: right;
  color: ${COLORS.success};
`

/** Scroll container so a long book scrolls while the header/footer stay put. */
export const ScrollArea = styled.div`
  max-height: 380px;
  overflow: auto;
`

/** The investor table. */
export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`

/** Sticky column-header row. */
export const HeadRow = styled.tr`
  text-align: left;
  color: ${COLORS.mutedText};
`

type CellProps = {align?: 'left' | 'right'}

/** Column header cell. */
export const Th = styled.th<CellProps>`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 8px 12px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-align: ${({align}) => align ?? 'left'};
  background-color: ${COLORS.cardBg};
  border-bottom: 1px solid ${COLORS.border};
`

/** One investor row. */
export const Row = styled.tr`
  &:hover {
    background-color: ${COLORS.mutedBg};
  }
`

/** Body cell. */
export const Td = styled.td<CellProps>`
  padding: 8px 12px;
  text-align: ${({align}) => align ?? 'left'};
  border-bottom: 1px solid ${COLORS.border};
  white-space: nowrap;
`

/** Investor name cell, emphasised. */
export const NameCell = styled(Td)`
  font-weight: 500;
  color: ${COLORS.foreground};
`

/** Funds cell, emphasised in the success colour. */
export const FundsCell = styled(Td)`
  font-weight: 600;
  color: ${COLORS.success};
`

/** Muted created-date cell. */
export const CreatedCell = styled(Td)`
  color: ${COLORS.mutedText};
`

/** Mailto link in the email column. */
export const EmailLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${COLORS.brand};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

/** Sticky totals row summing funds (= AUM) and share (= 100%). */
export const TotalRow = styled.tr`
  position: sticky;
  bottom: 0;
  font-weight: 600;
  color: ${COLORS.foreground};

  & > td {
    padding: 8px 12px;
    background-color: ${COLORS.cardBg};
    border-top: 1px solid ${COLORS.border};
  }
`

/** "Total" caption in the footer's first cell. */
export const TotalLabel = styled.td`
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${COLORS.mutedText};
`
